import { useState, useEffect } from 'react';
import { X, Plus, Car, Bike, Trash2, Edit2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { AddVehicleSheet } from './AddVehicleSheet';
import { useVehicleStore } from '../../store/vehicleStore';
import { CreateVehicleRequest } from '../../types';

interface Vehicle {
  id: string;
  type: '2wheeler' | '3wheeler' | '4wheeler';
  make: string;
  model: string;
  registrationNumber?: string;
  connectorTypes?: string[];
  maxChargingSpeedKw?: number;
  year?: number;
}

interface MyVehiclesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MyVehiclesSheet({ open, onOpenChange }: MyVehiclesSheetProps) {
  const {
    vehicles: apiVehicles,
    isLoading,
    error,
    fetchMyVehicles,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    clearError,
  } = useVehicleStore();

  const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  // Fetch vehicles when sheet opens
  useEffect(() => {
    if (open) {
      fetchMyVehicles();
    }
  }, [open, fetchMyVehicles]);

  // Transform API vehicles to local format for display
  const vehicles: Vehicle[] = apiVehicles.map((v) => ({
    id: v.id,
    type: (v.vehicleType || '4wheeler') as '2wheeler' | '3wheeler' | '4wheeler',
    make: v.make,
    model: v.model,
    registrationNumber: v.registrationNumber || '',
  }));

  if (!open) return null;

  const getVehicleIcon = (type: string) => {
    switch (type) {
      case '2wheeler':
        return <Bike className="w-6 h-6" />;
      case '3wheeler':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      case '4wheeler':
        return <Car className="w-6 h-6" />;
      default:
        return <Car className="w-6 h-6" />;
    }
  };

  const getVehicleTypeLabel = (type: string) => {
    switch (type) {
      case '2wheeler':
        return 'Two Wheeler';
      case '3wheeler':
        return 'Three Wheeler';
      case '4wheeler':
        return 'Four Wheeler';
      default:
        return type;
    }
  };

  const getVehicleColor = (type: string) => {
    switch (type) {
      case '2wheeler':
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case '3wheeler':
        return 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
      case '4wheeler':
        return 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      default:
        return 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  const handleAddVehicle = async (vehicle: Omit<Vehicle, 'id'>) => {
    try {
      // Transform to API format
      const payload: CreateVehicleRequest = {
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year || new Date().getFullYear(),
        vehicleType: vehicle.type,
        registrationNumber: vehicle.registrationNumber || undefined,
        connectorTypes: vehicle.connectorTypes || [],
        maxChargingSpeedKw: vehicle.maxChargingSpeedKw,
      };

      await addVehicle(payload);
      setIsAddVehicleOpen(false);
    } catch (err) {
      // Error handled by store
      console.error('Failed to add vehicle:', err);
    }
  };

  const handleEditVehicle = async (vehicle: Omit<Vehicle, 'id'>) => {
    if (editingVehicle) {
      try {
        await updateVehicle(editingVehicle.id, {
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          vehicleType: vehicle.type,
          registrationNumber: vehicle.registrationNumber || undefined,
          connectorTypes: vehicle.connectorTypes,
          maxChargingSpeedKw: vehicle.maxChargingSpeedKw,
        });
        setEditingVehicle(null);
        setIsAddVehicleOpen(false);
      } catch (err) {
        console.error('Failed to update vehicle:', err);
      }
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    try {
      await deleteVehicle(id);
    } catch (err) {
      console.error('Failed to delete vehicle:', err);
    }
  };

  const startEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setIsAddVehicleOpen(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
        <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Vehicles</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{vehicles.length} vehicle(s)</p>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  <Button
                    onClick={clearError}
                    className="text-red-600 hover:text-red-700 text-sm"
                    variant="ghost"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && vehicles.length === 0 ? (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 text-gray-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Loading vehicles...</p>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Car className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No Vehicles Added</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Add your vehicles to get personalized charger recommendations
                </p>
              </div>
            ) : (
              vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-4 hover:border-[#8bc43e] transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getVehicleColor(vehicle.type)}`}>
                      {getVehicleIcon(vehicle.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate">
                            {vehicle.make} {vehicle.model}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{getVehicleTypeLabel(vehicle.type)}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(vehicle)}
                            className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                          >
                            <Edit2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteVehicle(vehicle.id)}
                            className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </button>
                        </div>
                      </div>
                      {vehicle.registrationNumber && (
                        <div className="mt-2 inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-1.5">
                          <span className="text-xs font-mono font-semibold text-gray-700 dark:text-gray-300">
                            {vehicle.registrationNumber}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Info Card */}
            <div className="bg-gradient-to-br from-[#8bc43e]/10 to-[#6fa02e]/10 border border-[#8bc43e]/30 rounded-xl p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold">Tip:</span> Add your vehicles to filter charging stations that are compatible with your vehicle type.
              </p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] rounded-b-3xl">
            <Button
              onClick={() => {
                setEditingVehicle(null);
                setIsAddVehicleOpen(true);
              }}
              className="w-full h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add New Vehicle
            </Button>
          </div>
        </div>
      </div>

      {/* Add/Edit Vehicle Sheet */}
      <AddVehicleSheet
        open={isAddVehicleOpen}
        onOpenChange={(open) => {
          setIsAddVehicleOpen(open);
          if (!open) setEditingVehicle(null);
        }}
        onSave={editingVehicle ? handleEditVehicle : handleAddVehicle}
        editingVehicle={editingVehicle}
      />
    </>
  );
}